import * as React from 'react';
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, getGroupedRowModel, getExpandedRowModel } from '@tanstack/react-table';
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState, GroupingState, ExpandedState } from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ChevronDown,
  ChevronRight,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FieldSelector } from '@/components/TimesheetReports/FieldSelector';
import type { FieldConfig, FieldPreset } from '@/components/TimesheetReports/FieldSelector';

interface DataTableEnhancedProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchKey?: string;
  searchPlaceholder?: string;
  enableGrouping?: boolean;
  enableColumnVisibility?: boolean;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  pageSize?: number;
  onRowClick?: (row: TData) => void;
  customFilters?: React.ReactNode;
  storageKey?: string;
  fieldLabels?: Record<string, string>; // Custom labels for fields
  fieldCategories?: Record<string, string>; // Categories for fields
}

export function DataTableEnhanced<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = 'Search...',
  enableGrouping = true,
  enableColumnVisibility = true,
  enableSorting = true,
  enableFiltering = true,
  pageSize = 10,
  onRowClick,
  customFilters,
  storageKey,
  fieldLabels = {},
  fieldCategories = {},
}: DataTableEnhancedProps<TData, TValue>) {
  // Load preferences from localStorage
  const loadPreferences = () => {
    if (!storageKey) return null;
    try {
      const saved = localStorage.getItem(`datatable-${storageKey}`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedPrefs = loadPreferences();

  const [sorting, setSorting] = React.useState<SortingState>(savedPrefs?.sorting || []);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    savedPrefs?.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    savedPrefs?.columnVisibility || {}
  );
  const [grouping, setGrouping] = React.useState<GroupingState>(savedPrefs?.grouping || []);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [globalFilter, setGlobalFilter] = React.useState('');

  // Field presets state
  const [fieldPresets, setFieldPresets] = React.useState<FieldPreset[]>(() => {
    if (!storageKey) return [];
    try {
      const saved = localStorage.getItem(`field-presets-${storageKey}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save preferences to localStorage
  React.useEffect(() => {
    if (!storageKey) return;
    const prefs = {
      sorting,
      columnFilters,
      columnVisibility,
      grouping,
    };
    localStorage.setItem(`datatable-${storageKey}`, JSON.stringify(prefs));
  }, [sorting, columnFilters, columnVisibility, grouping, storageKey]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      grouping,
      expanded,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  // Create field configs from columns
  const fieldConfigs: FieldConfig[] = React.useMemo(() => {
    return table
      .getAllColumns()
      .filter((column) => column.getCanHide())
      .map((column, index) => ({
        id: column.id,
        label: fieldLabels[column.id] || column.id,
        visible: column.getIsVisible(),
        order: index,
        category: fieldCategories[column.id],
      }));
  }, [table, fieldLabels, fieldCategories]);

  const handleFieldsChange = (fields: FieldConfig[]) => {
    // Update column visibility based on field configs
    const newVisibility: VisibilityState = {};
    fields.forEach((field) => {
      newVisibility[field.id] = field.visible;
    });
    setColumnVisibility(newVisibility);

    // Reorder columns based on field order
    // Note: TanStack Table doesn't support dynamic column ordering out of the box
    // This would require additional implementation if needed
  };

  const handleSavePreset = (preset: FieldPreset) => {
    const newPresets = [...fieldPresets, preset];
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(`field-presets-${storageKey}`, JSON.stringify(newPresets));
    }
  };

  const handleDeletePreset = (presetId: string) => {
    const newPresets = fieldPresets.filter((p) => p.id !== presetId);
    setFieldPresets(newPresets);
    if (storageKey) {
      localStorage.setItem(`field-presets-${storageKey}`, JSON.stringify(newPresets));
    }
  };

  const resetPreferences = () => {
    setSorting([]);
    setColumnFilters([]);
    setColumnVisibility({});
    setGrouping([]);
    setGlobalFilter('');
    if (storageKey) {
      localStorage.removeItem(`datatable-${storageKey}`);
    }
  };

  const hasActiveFilters = columnFilters.length > 0 || globalFilter.length > 0;
  const hasActiveGrouping = grouping.length > 0;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-1 items-center gap-2">
          {/* Global Search */}
          {enableFiltering && searchKey && (
            <div className="relative flex-1 max-w-sm">
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter ?? ''}
                onChange={(event) => setGlobalFilter(event.target.value)}
                className="pr-8"
              />
              {globalFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                  onClick={() => setGlobalFilter('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}

          {/* Custom Filters */}
          {customFilters}

          {/* Active Filters Badge */}
          {hasActiveFilters && (
            <Badge variant="secondary" className="gap-1">
              {columnFilters.length + (globalFilter ? 1 : 0)} filter(s)
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => {
                  setColumnFilters([]);
                  setGlobalFilter('');
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {/* Active Grouping Badge */}
          {hasActiveGrouping && (
            <Badge variant="secondary" className="gap-1">
              Grouped by {grouping.length} column(s)
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => setGrouping([])}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Reset Button */}
          {(hasActiveFilters || hasActiveGrouping || sorting.length > 0) && (
            <Button variant="outline" size="sm" onClick={resetPreferences}>
              Reset All
            </Button>
          )}

          {/* Enhanced Field Selector */}
          {enableColumnVisibility && (
            <FieldSelector
              fields={fieldConfigs}
              onFieldsChange={handleFieldsChange}
              storageKey={storageKey}
              presets={fieldPresets}
              onSavePreset={handleSavePreset}
              onDeletePreset={handleDeletePreset}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {enableSorting && header.column.getCanSort() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {header.column.getIsSorted() === 'asc' ? (
                                <ArrowUp className="h-4 w-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ArrowDown className="h-4 w-4" />
                              ) : (
                                <ArrowUpDown className="h-4 w-4 opacity-50" />
                              )}
                            </Button>
                          )}
                          {enableGrouping && header.column.getCanGroup() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={header.column.getToggleGroupingHandler()}
                            >
                              {header.column.getIsGrouped() ? '🚫' : '📁'}
                            </Button>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    onRowClick && 'cursor-pointer hover:bg-muted/50',
                    row.getIsGrouped() && 'bg-muted/30 font-medium'
                  )}
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {cell.getIsGrouped() ? (
                        // Grouped cell
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              row.getToggleExpandedHandler()();
                            }}
                          >
                            {row.getIsExpanded() ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())} (
                          {row.subRows.length})
                        </div>
                      ) : cell.getIsAggregated() ? (
                        // Aggregated cell
                        flexRender(
                          cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                          cell.getContext()
                        )
                      ) : cell.getIsPlaceholder() ? null : (
                        // Normal cell
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} row(s) total
          {table.getFilteredRowModel().rows.length !== data.length &&
            ` (filtered from ${data.length})`}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <div className="text-sm">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
